import DocumentButton from '@/components/dataDisplay/DocumentButton';
import ExternalLink from '@/components/dataDisplay/ExternalLink';
import Markdown from '@/components/dataDisplay/Markdown';
import { Asset } from '@/modules/core/data/types/Asset';
import { CustomerTheme } from '@/modules/core/data/types/CustomerTheme';
import { EducationalProgramStep } from '@/modules/core/data/types/EducationalProgramStep';
import {
  findNextItem,
  findPreviousItem,
} from '@/modules/core/js/array';
import EducationalProgramStepsFeesNavBar from '@/modules/solutions/components/EducationalProgramStepsFeesNavBar';
import { getStepFullLabel } from '@/modules/solutions/educationalProgramStep';
import {
  css,
  useTheme,
} from '@emotion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classnames from 'classnames';
import get from 'lodash.get';
import map from 'lodash.map';
import React, {
  Dispatch,
  SetStateAction,
  useState,
} from 'react';
import {
  Trans,
  useTranslation,
} from 'react-i18next';

type Props = {
  activeEducationalProgramStep: EducationalProgramStep;
  selectedEducationalProgramStepsWithTuitionFees: EducationalProgramStep[];
  setActiveEducationalProgramStep: Dispatch<SetStateAction<EducationalProgramStep>>;
  setIsDisplayingFees: Dispatch<SetStateAction<boolean>>;
}

/**
 * Displays the tuition fees.
 *
 * Displays a navbar to navigate between previous/next steps, when multiple steps are being selected.
 */
const TuitionFeeContainer: React.FunctionComponent<Props> = (props): JSX.Element => {
  const {
    activeEducationalProgramStep,
    setActiveEducationalProgramStep,
    selectedEducationalProgramStepsWithTuitionFees,
    setIsDisplayingFees,
  } = props;
  const { t } = useTranslation();
  const theme: CustomerTheme = useTheme();
  const { surfaceColor } = theme;
  const [transition, setTransition] = useState<string>(null);

  const activeStepFeesDescription: string = get(activeEducationalProgramStep, 'tuitionFeesDescription', t('solutionsPage.programStepFees.feesDescriptionMissing.text', 'Aucune information'));
  const activeStepDocuments: Asset[] = get(activeEducationalProgramStep, 'tuitionFeesDocuments', []);
  const previousStep: EducationalProgramStep = findPreviousItem(activeEducationalProgramStep, selectedEducationalProgramStepsWithTuitionFees);
  const nextStep: EducationalProgramStep = findNextItem(activeEducationalProgramStep, selectedEducationalProgramStepsWithTuitionFees);

  return (
    <div
      css={css`
        position: relative;
        background-color: ${surfaceColor};
        border-radius: 25px;
        margin-left: 20px;
        margin-right: 10px;
        display: inline-flex;
        flex-wrap: wrap;
        justify-content: space-between;
        flex-direction: row;
        align-items: center;
        width: 97%;

        @media (max-width: 991.98px) {
          max-width: 90%;
        }

        .tuition-fees-title {
          display: flex;
          margin-top: 20px;
        }

        .tuition-fees-description {
          max-width: 70%;
          padding-left: 15px;
          padding-right: 10px;

          @media (max-width: 991.98px) {
            max-width: 90%;
          }
        }

        .tuition-fees-assets {
          display: flex;
          flex-direction: column;

          @media (max-width: 991.98px) {
            width: 100%;
            justify-content: center;
            align-items: center;
            text-align: center;
            margin-bottom: 20px;
          }
        }

        .animated {
          animation: ease-in-out;
          animation-duration: 400ms;
          animation-fill-mode: both;
        }

        .fadeInUp {
          opacity: 0;
          animation-name: fadeInUp;

          @keyframes fadeInUp {
            from {
              transform: translate3d(0, -40px, 0)
            }
            to {
              transform: translate3d(0, 0, 0);
              opacity: 1
            }
          }
        }

        .fadeInDown {
          opacity: 0;
          animation-name: fadeInDown;

          @keyframes fadeInDown {
            from {
              transform: translate3d(0, 40px, 0)
            }
            to {
              transform: translate3d(0, 0, 0);
              opacity: 1
            }
          }
        }

        .close {
          position: absolute;
          right: 10px;
          top: 10px;
        }
      `}
    >
      <EducationalProgramStepsFeesNavBar
        selectedEducationalProgramSteps={selectedEducationalProgramStepsWithTuitionFees}
        setActiveEducationalProgramStep={setActiveEducationalProgramStep}
        setTransition={setTransition}
        previousStep={previousStep}
        nextStep={nextStep}
      />

      <div
        className={classnames('tuition-fees-description', {
          'fadeInUp animated': transition === 'up',
          'fadeInDown animated': transition === 'down',
        })}
      >
        <div className={'tuition-fees-title'}>
          <FontAwesomeIcon
            icon="info-circle"
          />
          <h3>
            <Trans
              i18nKey={'solutionsPage.programStepFees.title'}
            >
              Frais de scolarité pour <b>{{ label: getStepFullLabel(activeEducationalProgramStep, { preferShortProgramLabel: false }) }}</b>
            </Trans>
          </h3>
        </div>
        <Markdown text={activeStepFeesDescription} />
      </div>

      {
        activeStepDocuments ? (
          <div className={'tuition-fees-assets'}>
            {
              map(activeStepDocuments, (asset: Asset) => {
                return (
                  <ExternalLink
                    href={asset?.assetUrl}
                    key={asset?.id}
                  >
                    <DocumentButton>
                      {asset?.title}
                    </DocumentButton>
                  </ExternalLink>
                );
              })
            }
          </div>
        ) : null
      }

      <button
        className={'close'}
        onClick={() => setIsDisplayingFees(false)}
      >
        <FontAwesomeIcon icon="times" />
      </button>
    </div>
  );
};

export default TuitionFeeContainer;
